import {Breadcrumb, Button, Checkbox, Form, Input, InputNumber, message, Select} from "antd";
import AiTier from "../../domain/AiTier";
import {useForm} from "antd/es/form/Form";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {AiTierServiceContext, AppContextContext, OrganizationServiceContext, OrganizationTagServiceContext} from "../../Contexts";
import QueryOptions from "../../sal-ui/QueryOptions";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import {DocumentTitle} from "../DocumentTitle";
import * as globalStyles from "../App.module.css";
import {Feature, formatFeature} from "../../domain/Feature";
import {formatCreditsCompact} from "../../utils/FormatUtils";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

function OrganizationAdd() {
    const appContext = useContext(AppContextContext);
    const organizationService = useContext(OrganizationServiceContext);
    const organizationTagService = useContext(OrganizationTagServiceContext);
    const aiTierService = useContext(AiTierServiceContext);
    const navigate = useNavigate();
    const [form] = useForm();
    const [aiTiers, setAiTiers] = useState<AiTier[]>([]);
    const [tags, setTags] = useState<string[]>([]);
    const selectedFeatures: Feature[] = Form.useWatch("features", form) || [];
    const selectedAiTierId: string | undefined = Form.useWatch("aiTierId", form);
    const allowAiTierLimitExceeded: boolean = Form.useWatch("allowAiTierLimitExceeded", form) || false;
    const selectedAiTier = aiTiers.find(tier => tier.id === selectedAiTierId);

    useEffect(() => {
        aiTierService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setAiTiers(result.data));

        organizationTagService.getList()
            .then(setTags);
    }, []);

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 16},
    };

    const title = "New organization";

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to={"/organizations"}>{"Organizations"}</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>{title}</Breadcrumb.Item>
                </Breadcrumb>

                <h1>{title}</h1>

                <Form {...layout} form={form} className={`${globalStyles["common__form"]}`} onFinish={onFinish}>
                    <Form.Item
                        name={"name"}
                        label={"Name"}
                        rules={[
                            {required: true, message: "Name is required."},
                            {validator: serverViolationsHolder.createServerValidator('UNIQUE', 'name', undefined, {compareLowerCaseValues: true}), message: "Name is already used."}
                        ]}>
                        <Input autoFocus={true} maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"organizationId"}
                        label={"Organization ID"}
                        rules={[
                            {required: true, message: "Organization ID is required."},
                            {validator: serverViolationsHolder.createServerValidator('UNIQUE', 'organizationId', undefined, {compareLowerCaseValues: true}), message: "Organization ID is already used."}
                        ]}>
                        <Input maxLength={50}/>
                    </Form.Item>

                    <Form.Item
                        name={["features"]}
                        label={"Features"}
                        rules={[{required: true, message: "Please select at least one feature."}]}>
                        <SelectAllMultiSelect
                            selectAllValues={Object.values(Feature)}
                            options={Object.values(Feature).map(feature => ({
                                label: formatFeature(feature),
                                value: feature
                            }))}
                        />
                    </Form.Item>

                    <Form.Item name={["tags"]} label={"Tags"}>
                        <Select mode="tags" options={tags.map(tag => ({value: tag, label: tag}))}/>
                    </Form.Item>

                    {selectedFeatures.includes(Feature.AI) && (
                        <>
                            <Form.Item
                                name={"aiTierId"}
                                label={"AI tier"}
                                rules={[{required: true, message: "AI tier is required when AI feature is enabled."}]}>
                                <Select
                                    allowClear
                                    options={aiTiers.map(tier => ({
                                        label: formatAiTierOption(tier),
                                        value: tier.id
                                    }))}
                                />
                            </Form.Item>

                            <Form.Item
                                name={"allowAiTierLimitExceeded"}
                                label={"AI credits"}
                                valuePropName={"checked"}>
                                <Checkbox>Allow usage above tier limit</Checkbox>
                            </Form.Item>

                            {allowAiTierLimitExceeded && (
                                <Form.Item
                                    name={"aiHardCreditLimitPerMonth"}
                                    label={"Monthly hard limit"}
                                    rules={[
                                        {required: true, message: "Monthly hard limit is required when usage above tier limit is allowed."},
                                        {validator: validateHardCreditLimit}
                                    ]}>
                                    <InputNumber min={0} precision={2}/>
                                </Form.Item>
                            )}
                        </>
                    )}

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>Save</Button>
                        <Button onClick={() => navigate("/organizations")}>Cancel</Button>
                    </Form.Item>
                </Form>
            </>
        </DocumentTitle>
    )

    function onFinish(values: any) {
        if (!values.features?.includes(Feature.AI)) {
            values.aiTierId = undefined;
            values.allowAiTierLimitExceeded = false;
            values.aiHardCreditLimitPerMonth = undefined;
        } else if (!values.allowAiTierLimitExceeded) {
            values.aiHardCreditLimitPerMonth = undefined;
        }

        organizationService.add(values)
            .then(
                (id) => {
                    message.success(<>Organization <b>{values.name}</b> successfully added.</>);

                    navigate(`/organizations/${id}`);
                },
                error => serverViolationsHolder.handleServerError(error, form)
            );
    }

    function validateHardCreditLimit(_: unknown, value?: number) {
        const tierLimit = selectedAiTier?.maxCreditsPerMonth;

        if (!allowAiTierLimitExceeded || tierLimit == null || value == null) {
            return Promise.resolve();
        }

        if (value < tierLimit) {
            return Promise.reject(new Error("Monthly hard limit must be greater than or equal to AI tier limit."));
        }

        return Promise.resolve();
    }

    function formatAiTierOption(tier: AiTier) {
        return `${tier.name} (${formatCreditsCompact(tier.maxCreditsPerMonth)})`;
    }
}

export default OrganizationAdd;
